import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, FileSpreadsheet, Link2, Upload, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Step = ({ n, children }) => (
  <li className="flex gap-2"><span className="w-5 h-5 shrink-0 rounded-full bg-[#FF4500] text-white text-xs font-bold flex items-center justify-center">{n}</span><span>{children}</span></li>
);

const MODES = {
  map: {
    title: "Importeer KML-kaart", accept: ".kml,.kmz", fileLabel: "…of kies een .kml/.kmz-bestand",
    urlLabel: "Link van de kaart", placeholder: "https://www.google.com/maps/d/viewer?mid=…",
    desc: "Zet een kaart (bijv. Google My Maps met alle tennisverenigingen) om naar een Excel. Er wordt nog niets opgeslagen.",
    step1: "Plak de link van de kaart, of kies een .kml/.kmz-bestand.", unit: "punten",
  },
  pdf: {
    title: "Importeer PDF", accept: ".pdf", fileLabel: "Kies een .pdf-bestand (met echte tekst, geen scan)",
    urlLabel: "…of link naar een pdf", placeholder: "https://…/verenigingen.pdf",
    desc: "Lees een pdf met een lijst verenigingen uit: naam, adres, postcode, plaats, e-mail, telefoon en de link achter 'Website'. Er wordt nog niets opgeslagen.",
    step1: "Kies de pdf (of plak een link ernaar).", unit: "verenigingen",
  },
};

export const MapToExcel = ({ open, onOpenChange, profile, profileLabel, onOpenUpload, mode = "map" }) => {
  const M = MODES[mode];
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [links, setLinks] = useState([]);
  const loadLinks = () => axios.get(`${API}/leads/map-links`, { params: { profile, kind: mode } }).then(({ data }) => setLinks(data)).catch(() => setLinks([]));
  useEffect(() => { if (open) loadLinks(); }, [open, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const removeLink = async (l) => {
    if (!window.confirm(`Kaartlink "${l.title}" vergeten?`)) return;
    await axios.delete(`${API}/leads/map-links/${l.id}`); loadLinks();
  };

  const run = async (opts = {}) => {
    const theUrl = opts.url ?? url.trim();
    if (!theUrl && !file) return toast.error(mode === "pdf" ? "Kies een pdf of plak een link" : "Plak een kaartlink of kies een bestand");
    const fd = new FormData();
    if (file && !opts.url) fd.append("file", file); else fd.append("url", theUrl);
    if (opts.onlyNew) fd.append("only_new", "true");
    setBusy(true); setDone(null);
    try {
      const res = await axios.post(`${API}/leads/map-to-excel`, fd, { params: { profile }, responseType: "blob" });
      const points = Number(res.headers["x-points"] || 0);
      const known = Number(res.headers["x-known"] || 0);
      const rows = Number(res.headers["x-rows"] || points);
      const before = Number(res.headers["x-seen-before"] || 0);
      if (opts.onlyNew && rows === 0) { toast.info("Geen nieuwe clubs op deze kaart sinds de vorige import"); loadLinks(); return; }
      const href = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = href; a.download = `${mode === "pdf" ? "pdf" : "kaart"}_${profile}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(href);
      setDone({ points, known, rows, before, onlyNew: !!opts.onlyNew });
      toast.success(`Excel met ${rows} rij(en) gedownload`);
      loadLinks();
    } catch (e) {
      let msg = mode === "pdf" ? "PDF omzetten mislukt" : "Kaart omzetten mislukt";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const urlField = (
        <label className="block text-xs font-semibold text-slate-600">{M.urlLabel}
          <div className="mt-1 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-slate-400" />
            <Input data-testid="map-excel-url" placeholder={M.placeholder} value={url}
              onChange={(e) => { setUrl(e.target.value); setFile(null); }} className="text-sm" />
          </div>
        </label>
  );
  const filePicker = (
        <label className="flex items-center gap-3 rounded-lg border-2 border-dashed border-slate-300 p-3 cursor-pointer hover:border-[#FF4500] transition-colors">
          <FileSpreadsheet className="w-5 h-5 text-[#FF4500]" />
          <span className="text-sm text-slate-700">{file ? file.name : M.fileLabel}</span>
          <input data-testid={mode === "pdf" ? "pdf-excel-file" : "map-excel-file"} type="file" accept={M.accept} className="hidden"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setUrl(""); e.target.value = ""; }} />
        </label>
  );

  const close = (v) => { if (!v) { setUrl(""); setFile(null); setDone(null); } onOpenChange(v); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid={mode === "pdf" ? "pdf-excel-dialog" : "map-excel-dialog"} className="max-w-lg max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{M.title}</DialogTitle>
          <DialogDescription>{M.desc}</DialogDescription>
        </DialogHeader>
        <ol className="space-y-1.5 text-sm text-slate-700">
          <Step n={1}>{M.step1}</Step>
          <Step n={2}>Download de Excel en corrigeer die handmatig.</Step>
          <Step n={3}>Upload de gecorrigeerde Excel via <b>Excel uploaden</b> → de leads komen in <b>{profileLabel}</b>.</Step>
        </ol>
        {mode === "pdf" ? <>{filePicker}{urlField}</> : <>{urlField}{filePicker}</>}
        {links.length > 0 && (
          <div data-testid="map-links" className="rounded-lg border border-slate-200">
            <div className="px-3 py-2 text-xs font-semibold text-slate-600 border-b border-slate-100">{mode === "pdf" ? "Eerder gebruikte pdf-links" : "Eerder gebruikte kaarten"}</div>
            <ul className="divide-y divide-slate-100 max-h-44 overflow-auto">
              {links.map((l, i) => (
                <li key={l.id} data-testid={`map-link-${i}`} className="flex items-center gap-2 px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate" title={l.url}>{l.title || l.url}</div>
                    <div className="text-xs text-slate-400">{l.count} {M.unit} · laatst {new Date(l.last_at).toLocaleDateString("nl-NL")}</div>
                  </div>
                  <Button data-testid={`map-link-rerun-${i}`} size="sm" variant="outline" disabled={busy} onClick={() => run({ url: l.url, onlyNew: true })} className="rounded-full h-8" title="Alleen clubs die er sinds de vorige import bij zijn gekomen">
                    <RefreshCw className="w-3.5 h-3.5 mr-1" /> Opnieuw (nieuwe)
                  </Button>
                  <button data-testid={`map-link-all-${i}`} disabled={busy} onClick={() => run({ url: l.url })} className="text-xs text-slate-500 hover:text-slate-900 underline">alles</button>
                  <button data-testid={`map-link-delete-${i}`} onClick={() => removeLink(l)} title="Vergeten" className="text-slate-300 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <Button data-testid="map-excel-download" onClick={() => run()} disabled={busy} className="rounded-full bg-[#FF4500] hover:bg-[#E63E00] text-white">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Download als Excel
        </Button>
        {done && (
          <div data-testid="map-excel-result" className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900 space-y-2">
            {done.onlyNew ? (
              <p><b>{done.rows}</b> nieuwe club(s) van de {done.points} punten. {done.known} bekend overgeslagen{done.before ? <> (waarvan {done.before} herkend op locatie, ook als de naam anders is)</> : null}.</p>
            ) : (
              <p><b>{done.points}</b> {M.unit} in de Excel{done.known ? <> — <b>{done.known}</b> staan al in de app (kolom Bron: "staat al in app") en worden bij upload overgeslagen</> : null}.</p>
            )}
            <Button data-testid="map-excel-open-upload" size="sm" variant="outline" onClick={() => { close(false); onOpenUpload(); }} className="rounded-full">
              <Upload className="w-4 h-4 mr-1.5" /> Gecorrigeerde Excel uploaden
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
